import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getPatients,
  createPatient,
  updatePatient,
  archivePatient,
  restorePatient,
  getDashboardStats,
  getUsDemoDashboard,
  getUsDemoAwvClinicalSummaryPDF,
  getUsDemoPersonalizedPreventionPlanPDF,
  getUsDemoPatientCaregiverSummaryPDF,
} from "../lib/api";
import { UpgradePrompt } from "../components/UpgradePrompt";
import { EmptyState } from "../components/EmptyState";
import DemoTooltip from "../components/DemoTooltip";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { MultiSelect } from "../components/ui/multi-select";
import { toast } from "sonner";
import {
  Search,
  Plus,
  User,
  Phone,
  AlertTriangle,
  ArrowRight,
  Archive,
  ArchiveRestore,
  Loader2,
  Edit,
  Activity,
  Filter
} from "lucide-react";
import { isUsClinicalHost, isUsDemoMode as detectUsDemoMode } from "../lib/usClinicalHost";

// Lista de 18 condiciones crónicas predefinidas para CDSS
const CHRONIC_CONDITIONS_LIST = [
  "Hipertensión arterial",
  "Diabetes mellitus tipo 2",
  "Dislipidemia",
  "Insuficiencia cardiaca",
  "Cardiopatía isquémica",
  "Enfermedad renal crónica",
  "EPOC",
  "Asma",
  "Osteoporosis",
  "Artrosis",
  "Demencia",
  "Depresión",
  "Parkinson",
  "ACV previo",
  "Obesidad",
  "Desnutrición",
  "Hipotiroidismo",
  "Anemia"
];

const RiskBadge = ({ level }) => {
  const styles = {
    alto: "bg-red-100 text-red-800 border-red-200",
    medio: "bg-amber-100 text-amber-800 border-amber-200",
    bajo: "bg-emerald-100 text-emerald-800 border-emerald-200"
  };
  const labels = { alto: "Alto", medio: "Medio", bajo: "Bajo" };
  
  return (
    <Badge variant="outline" className={`${styles[level]} border text-xs`}>
      {labels[level]}
    </Badge>
  );
};

const emptyPatient = {
  nombres: "",
  apellidos: "",
  fecha_nacimiento: "",
  telefono: "",
  direccion: "",
  cuidador_principal: "",
  telefono_cuidador: "",
  diagnostico_principal: "",
  alergias: "",
  chronic_conditions: [], // Multi-select array para CDSS
  medicamentos_actuales: ""
};

const coercePatientsArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.patients)) return payload.patients;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const coerceStringArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map((v) => String(v).trim()).filter(Boolean);
  if (typeof value === "string") return value.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
};

const patientMutationErrorMessage = (error, isEn) => {
  const code = error?.response?.status;
  if (code === 400) return isEn ? "Invalid patient identifier." : "Identificador de paciente no válido.";
  if (code === 404) return isEn ? "Patient not found." : "Paciente no encontrado.";
  if (code === 409) return isEn ? "This patient is archived." : "El paciente está archivado.";
  if (!error?.response) return isEn ? "Network error. Try again." : "Error de red. Intente de nuevo.";
  return isEn ? "Unable to complete the request." : "No se pudo completar la operación.";
};

export default function Patients() {
  const { isPremium, user } = useAuth();
  const isUsHost = isUsClinicalHost();
  const isUsDemoMode = detectUsDemoMode();
  const [patients, setPatients] = useState([]);
  const [usDashboardStats, setUsDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [listStatus, setListStatus] = useState("active");
  const [usFilter, setUsFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [formData, setFormData] = useState(emptyPatient);
  const isDemoUser = user?.email === "demo@aurorageriatrica.com";

  const openDemoPdf = async (demoVisitId, kind) => {
    if (!demoVisitId) return;
    const loaders = {
      clinical: getUsDemoAwvClinicalSummaryPDF,
      prevention: getUsDemoPersonalizedPreventionPlanPDF,
      caregiver: getUsDemoPatientCaregiverSummaryPDF,
    };
    const loader = loaders[kind];
    if (!loader) return;
    try {
      const res = await loader(demoVisitId);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Unable to open demo document");
    }
  };

  const fetchPatients = useCallback(async () => {
    try {
      if (isUsHost && isUsDemoMode) {
        const dashboardRes = await getUsDemoDashboard();
        const demoPatients = Array.isArray(dashboardRes?.data?.demo_patients) ? dashboardRes.data.demo_patients : [];
        const mappedDemoPatients = demoPatients.map((p) => {
          const [first = "", ...rest] = String(p?.name || "").split(" ");
          return {
            id: p?.id,
            nombres: first,
            apellidos: rest.join(" "),
            edad: p?.age ?? "—",
            diagnostico_principal: p?.primary_case || "",
            telefono: "",
            chronic_conditions: [],
          };
        });
        setPatients(mappedDemoPatients);
        setUsDashboardStats(dashboardRes?.data ?? null);
        return;
      }

      const [patientsRes, dashboardRes] = await Promise.all([
        getPatients(listStatus),
        isUsHost ? getDashboardStats() : Promise.resolve({ data: null }),
      ]);
      setPatients(coercePatientsArray(patientsRes?.data));
      setUsDashboardStats(isUsHost ? (dashboardRes?.data ?? null) : null);
    } catch (error) {
      toast.error(isUsHost ? "Unable to load the patient panel." : "Error al cargar pacientes");
      setPatients([]);
      setUsDashboardStats(null);
    } finally {
      setLoading(false);
    }
  }, [isUsDemoMode, isUsHost, listStatus]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const openNewPatientDialog = () => {
    setEditingPatient(null);
    setFormData(emptyPatient);
    setIsDialogOpen(true);
  };

  const openEditPatientDialog = (patient) => {
    setEditingPatient(patient);
    setFormData({
      nombres: patient.nombres || "",
      apellidos: patient.apellidos || "",
      fecha_nacimiento: patient.fecha_nacimiento || "",
      telefono: patient.telefono || "",
      direccion: patient.direccion || "",
      cuidador_principal: patient.cuidador_principal || "",
      telefono_cuidador: patient.telefono_cuidador || "",
      diagnostico_principal: patient.diagnostico_principal || "",
      alergias: coerceStringArray(patient?.alergias).join(", "),
      chronic_conditions: coerceStringArray(patient?.chronic_conditions),
      medicamentos_actuales: coerceStringArray(patient?.medicamentos_actuales).join(", ")
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDemoUser) { toast.error("El demo es de solo lectura. Crea tu cuenta para editar pacientes."); return; }
    setSaving(true);
    try {
      const patientData = {
        ...formData,
        alergias: formData.alergias.split(",").map(s => s.trim()).filter(Boolean),
        chronic_conditions: formData.chronic_conditions, // Ya es un array
        medicamentos_actuales: formData.medicamentos_actuales.split(",").map(s => s.trim()).filter(Boolean)
      };
      
      if (editingPatient) {
        await updatePatient(editingPatient.id, patientData);
        toast.success(isUsHost ? "Patient record updated" : "Paciente actualizado");
      } else {
        if (isDemoUser) { toast.error("El demo es de solo lectura. Crea tu cuenta para gestionar pacientes."); return; }
        await createPatient(patientData);
        toast.success(isUsHost ? "Patient record created" : "Paciente registrado");
      }
      
      setIsDialogOpen(false);
      setFormData(emptyPatient);
      setEditingPatient(null);
      fetchPatients();
    } catch (error) {
      toast.error(
        editingPatient
          ? (isUsHost ? "Unable to update patient record." : "Error al actualizar")
          : (isUsHost ? "Unable to create patient record." : "Error al registrar")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleArchivePatient = async (id) => {
    if (isDemoUser) { toast.error("El demo es de solo lectura. Crea tu cuenta para gestionar pacientes."); return; }
    if (!window.confirm(isUsHost
      ? "Archive this patient? Their record and consultations will be preserved. New consultations will be disabled until the patient is restored."
      : "¿Archivar este paciente? Su expediente y consultas se conservarán. No se podrán crear nuevas consultas hasta restaurarlo.")) return;
    try {
      await archivePatient(id);
      setPatients((prev) => (Array.isArray(prev) ? prev.filter((p) => p?.id !== id) : []));
      toast.success(isUsHost ? "Patient archived. The record was preserved." : "Paciente archivado. El expediente se conservó.");
    } catch (error) {
      toast.error(patientMutationErrorMessage(error, isUsHost));
    }
  };

  const handleRestorePatient = async (id) => {
    if (isDemoUser) { toast.error("El demo es de solo lectura. Crea tu cuenta para gestionar pacientes."); return; }
    if (!window.confirm(isUsHost
      ? "Restore this patient? They will return to the active list and may receive new consultations."
      : "¿Restaurar este paciente? Volverá a la lista de pacientes activos y podrá recibir nuevas consultas.")) return;
    try {
      await restorePatient(id);
      setPatients((prev) => (Array.isArray(prev) ? prev.filter((p) => p?.id !== id) : []));
      toast.success(isUsHost ? "Patient restored." : "Paciente restaurado.");
    } catch (error) {
      toast.error(patientMutationErrorMessage(error, isUsHost));
    }
  };

  const safePatients = Array.isArray(patients) ? patients : [];

  const filteredPatients = safePatients.filter((p) => {
    const nombres = String(p?.nombres ?? "");
    const apellidos = String(p?.apellidos ?? "");
    const fullName = `${nombres} ${apellidos}`.toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) ||
           String(p?.diagnostico_principal ?? "").toLowerCase().includes(search);
  });

  const usHighRiskMap = new Map(
    (Array.isArray(usDashboardStats?.high_risk_flags) ? usDashboardStats.high_risk_flags : []).map((row) => [row.patient_id, row])
  );
  const usAwvDueSet = new Set((Array.isArray(usDashboardStats?.awv_due_patients) ? usDashboardStats.awv_due_patients : []).map((row) => row.patient_id));
  const usFollowUpSet = new Set((Array.isArray(usDashboardStats?.follow_up_needed) ? usDashboardStats.follow_up_needed : []).map((row) => row.patient_id));
  const usRows = filteredPatients.map((p) => {
    const risk = usHighRiskMap.get(p?.id) || {};
    const flags = Array.isArray(risk?.flags) ? risk.flags : [];
    const matchingVisit = (Array.isArray(usDashboardStats?.recent_visits) ? usDashboardStats.recent_visits : []).find((v) => v?.patient_id === p?.id);
    const steadiPlan = risk?.steadi || matchingVisit?.steadi || null;
    const cognitionPlan = risk?.cognition || matchingVisit?.cognition || null;
    const moodPlan = risk?.mood || matchingVisit?.mood || null;
    const functionalPlan = risk?.functional || matchingVisit?.functional || null;
    const frailtyPlan = risk?.frailty || matchingVisit?.frailty || null;
    const medicationReview = risk?.medication_review || matchingVisit?.medication_review || null;
    const sdohPlan = risk?.sdoh_screening || matchingVisit?.sdoh_screening || null;
    return {
      id: p?.id,
      patient: `${p?.nombres ?? ""} ${p?.apellidos ?? ""}`.trim() || "Unknown patient",
      age: p?.edad ?? "—",
      lastVisit: matchingVisit?.created_at ? new Date(matchingVisit.created_at).toLocaleDateString("en-US") : "—",
      fallRisk: risk?.fall_risk_level || (flags.includes("Fall Risk") ? "moderate risk" : "low risk"),
      steadiFollowUp: steadiPlan?.follow_up_interval || "",
      steadiPriority: steadiPlan?.priority || "",
      cognitionPlan,
      functionalPlan,
      frailtyPlan,
      medicationReview,
      sdohPlan,
      riskLevel: (risk?.risk_score ?? 0) >= 3 ? "High" : (risk?.risk_score ?? 0) >= 1 ? "Moderate" : "Low",
      awvDue: usAwvDueSet.has(p?.id),
      followUpNeeded: usFollowUpSet.has(p?.id),
      highFallRisk: String(risk?.fall_risk_level || "").toLowerCase() === "high risk",
      cognitiveConcern: flags.includes("Cognitive Concern"),
      moodConcern: flags.includes("Mood Concern"),
      moodPlan,
      moodFollowUpOperational: Boolean(risk?.mood_follow_up),
      polypharmacy: flags.includes("Polypharmacy"),
      socialRiskConcern: flags.includes("Social Risk Concern"),
      demoVisitId: matchingVisit?.id || null,
      demoVisitHref: matchingVisit?.id ? `/nueva-consulta/${p?.id}?us_demo=1&demo_visit=${matchingVisit.id}` : null,
      functionalDeclineFlag: flags.includes("Functional Decline"),
    };
  });
  const filteredUsRows = usRows.filter((row) => {
    if (usFilter === "awv_due") return row.awvDue;
    if (usFilter === "follow_up") return row.followUpNeeded;
    if (usFilter === "high_fall_risk") return row.highFallRisk;
    if (usFilter === "cognitive_concern") return row.cognitiveConcern;
    return true;
  });

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-48 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (isUsHost) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="patients-us-panel">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Patients</h1>
          <p className="text-slate-600 text-sm">Risk-based geriatric panel</p>
          {!isUsDemoMode && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant={listStatus === "active" ? "default" : "outline"} size="sm" onClick={() => setListStatus("active")}>Active</Button>
              <Button variant={listStatus === "archived" ? "default" : "outline"} size="sm" onClick={() => setListStatus("archived")}>Archived</Button>
            </div>
          )}
          {listStatus === "archived" && !isUsDemoMode && (
            <p className="text-xs text-slate-500 max-w-2xl">
              Archived records keep their consultations and documents. Restore the patient to start a new consultation.
            </p>
          )}
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <Button variant={usFilter === "all" ? "default" : "outline"} size="sm" onClick={() => setUsFilter("all")}>All</Button>
              <Button variant={usFilter === "awv_due" ? "default" : "outline"} size="sm" onClick={() => setUsFilter("awv_due")}>AWV Due</Button>
              <Button variant={usFilter === "follow_up" ? "default" : "outline"} size="sm" onClick={() => setUsFilter("follow_up")}>Follow-Up Needed</Button>
              <Button variant={usFilter === "high_fall_risk" ? "default" : "outline"} size="sm" onClick={() => setUsFilter("high_fall_risk")}>High Fall Risk</Button>
              <Button variant={usFilter === "cognitive_concern" ? "default" : "outline"} size="sm" onClick={() => setUsFilter("cognitive_concern")}>Cognitive Concern</Button>
            </div>
          </CardContent>
        </Card>

        {filteredUsRows.length === 0 ? (
          <EmptyState
            icon={User}
            title="No patients in panel"
            description="No patients match the current operational filter."
            actionLabel="Add Patient"
            onAction={openNewPatientDialog}
          />
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b bg-slate-50/70">
                      <th className="py-3 px-3">Patient</th>
                      <th className="py-3 px-3">Age</th>
                      <th className="py-3 px-3">Last Visit</th>
                      <th className="py-3 px-3">Fall risk / STEADI</th>
                      <th className="py-3 px-3 min-w-[160px]">Frailty (screening)</th>
                      <th className="py-3 px-3 min-w-[160px]">Cognition (screening)</th>
                      <th className="py-3 px-3 min-w-[150px]">Mood (screening)</th>
                      <th className="py-3 px-3 min-w-[170px]">Functional (screening)</th>
                      <th className="py-3 px-3">Risk Level</th>
                      <th className="py-3 px-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsRows.map((row) => (
                      <tr key={row.id} className="border-b last:border-0">
                        <td className="py-3 px-3 font-medium text-slate-800">
                          {isUsDemoMode && row.demoVisitHref ? (
                            <Link to={row.demoVisitHref} className="text-sky-700 hover:text-sky-800 hover:underline">
                              {row.patient}
                            </Link>
                          ) : (
                            row.patient
                          )}
                        </td>
                        <td className="py-3 px-3 text-slate-600">{row.age}</td>
                        <td className="py-3 px-3 text-slate-600">{row.lastVisit}</td>
                        <td className="py-3 px-3 text-slate-600">
                          <div className="capitalize">{row.fallRisk}</div>
                          {(row.steadiFollowUp || row.steadiPriority) && (
                            <div className="mt-1 text-[11px] text-slate-500 space-y-0.5">
                              {row.steadiPriority && (
                                <div>
                                  <span className="font-semibold text-slate-600">Priority:</span>{" "}
                                  <span className="capitalize">{String(row.steadiPriority).replace(/_/g, " ")}</span>
                                </div>
                              )}
                              {row.steadiFollowUp && (
                                <div>
                                  <span className="font-semibold text-slate-600">STEADI follow-up:</span> {row.steadiFollowUp}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 text-slate-600 align-top">
                          <div className="space-y-1">
                            {row.frailtyPlan?.frailty_status != null && row.frailtyPlan.frailty_status !== "" ? (
                              <>
                                <Badge
                                  variant="outline"
                                  className={
                                    row.frailtyPlan.frailty_status === "robust"
                                      ? "text-[10px] border-slate-200 text-slate-700 bg-slate-50"
                                      : row.frailtyPlan.frailty_status === "pre_frail"
                                        ? "text-[10px] border-amber-200 text-amber-900 bg-amber-50"
                                        : "text-[10px] border-orange-200 text-orange-900 bg-orange-50"
                                  }
                                >
                                  {String(row.frailtyPlan.frailty_status || "").replace(/_/g, " ")}
                                </Badge>
                                {(row.frailtyPlan?.priority || row.frailtyPlan?.follow_up_interval) && (
                                  <div className="text-[11px] text-slate-600 mt-1 space-y-0.5">
                                    {row.frailtyPlan?.priority && (
                                      <div>
                                        <span className="font-semibold text-slate-700">Priority:</span>{" "}
                                        <span className="capitalize">{String(row.frailtyPlan.priority).replace(/_/g, " ")}</span>
                                      </div>
                                    )}
                                    {row.frailtyPlan?.follow_up_interval && (
                                      <div>
                                        <span className="font-semibold text-slate-700">Suggested follow-up:</span>{" "}
                                        {row.frailtyPlan.follow_up_interval}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-slate-500">—</span>
                            )}
                            {flags.includes("Frailty Concern") && (!row.frailtyPlan || row.frailtyPlan.frailty_status === "robust") && (
                              <div className="mt-1">
                                <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-800 bg-amber-50">
                                  Flag: frailty concern
                                </Badge>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-slate-600 align-top">
                          <div className="space-y-1">
                            {row.cognitionPlan?.cognitive_status && row.cognitionPlan.cognitive_status !== "normal" ? (
                              <>
                                <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-800 bg-purple-50">
                                  {String(row.cognitionPlan.cognitive_status || "").replace(/_/g, " ")}
                                </Badge>
                                {(row.cognitionPlan?.priority || row.cognitionPlan?.follow_up_interval) && (
                                  <div className="text-[11px] text-slate-600 mt-1 space-y-0.5">
                                    {row.cognitionPlan?.priority && (
                                      <div>
                                        <span className="font-semibold text-slate-700">Priority:</span>{" "}
                                        <span className="capitalize">{String(row.cognitionPlan.priority).replace(/_/g, " ")}</span>
                                      </div>
                                    )}
                                    {row.cognitionPlan?.follow_up_interval && (
                                      <div>
                                        <span className="font-semibold text-slate-700">Suggested follow-up:</span>{" "}
                                        {row.cognitionPlan.follow_up_interval}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-slate-500">Routine</span>
                            )}
                            {row.cognitiveConcern && (!row.cognitionPlan || row.cognitionPlan.cognitive_status === "normal") && (
                              <div className="mt-1">
                                <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-700 bg-purple-50/80">
                                  Flag: concern
                                </Badge>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-slate-600 align-top">
                          <div className="space-y-1">
                            {row.moodConcern ? (
                              <>
                                <Badge variant="outline" className="text-[10px] border-sky-200 text-sky-800 bg-sky-50">
                                  Screening concern
                                </Badge>
                                {(row.moodPlan?.priority || row.moodPlan?.follow_up_interval) && (
                                  <div className="text-[11px] text-slate-600 mt-1 space-y-0.5">
                                    {row.moodPlan?.priority && (
                                      <div>
                                        <span className="font-semibold text-slate-700">Priority:</span>{" "}
                                        <span className="capitalize">{String(row.moodPlan.priority).replace(/_/g, " ")}</span>
                                      </div>
                                    )}
                                    {row.moodPlan?.follow_up_interval && (
                                      <div>
                                        <span className="font-semibold text-slate-700">Suggested follow-up:</span>{" "}
                                        {row.moodPlan.follow_up_interval}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-slate-500">Routine</span>
                            )}
                            {row.moodFollowUpOperational && (
                              <div className="mt-1">
                                <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-800 bg-amber-50">
                                  Mood follow-up planned
                                </Badge>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-slate-600 align-top">
                          <div className="space-y-1">
                            {row.functionalPlan?.functional_status != null && row.functionalPlan.functional_status !== "" ? (
                              <>
                                <Badge
                                  variant="outline"
                                  className={
                                    row.functionalPlan.functional_status === "independent"
                                      ? "text-[10px] border-slate-200 text-slate-700 bg-slate-50"
                                      : "text-[10px] border-emerald-200 text-emerald-900 bg-emerald-50"
                                  }
                                >
                                  {String(row.functionalPlan.functional_status || "").replace(/_/g, " ")}
                                </Badge>
                                {(row.functionalPlan?.priority || row.functionalPlan?.follow_up_interval) && (
                                  <div className="text-[11px] text-slate-600 mt-1 space-y-0.5">
                                    {row.functionalPlan?.priority && (
                                      <div>
                                        <span className="font-semibold text-slate-700">Priority:</span>{" "}
                                        <span className="capitalize">{String(row.functionalPlan.priority).replace(/_/g, " ")}</span>
                                      </div>
                                    )}
                                    {row.functionalPlan?.follow_up_interval && (
                                      <div>
                                        <span className="font-semibold text-slate-700">Suggested follow-up:</span>{" "}
                                        {row.functionalPlan.follow_up_interval}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-slate-500">—</span>
                            )}
                            {row.functionalDeclineFlag && (!row.functionalPlan || row.functionalPlan.functional_status === "independent") && (
                              <div className="mt-1">
                                <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-800 bg-amber-50">
                                  Flag: functional concern
                                </Badge>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <Badge variant="outline" className={row.riskLevel === "High" ? "border-red-200 text-red-700 bg-red-50" : row.riskLevel === "Moderate" ? "border-amber-200 text-amber-700 bg-amber-50" : "border-emerald-200 text-emerald-700 bg-emerald-50"}>
                            {row.riskLevel}
                          </Badge>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {row.awvDue && <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700 bg-amber-50">AWV Due</Badge>}
                            {row.polypharmacy && <Badge variant="outline" className="text-[10px] border-rose-200 text-rose-700 bg-rose-50">Polypharmacy</Badge>}
                            {row.polypharmacy && row.medicationReview?.priority && (
                              <Badge variant="outline" className="text-[10px] border-rose-200 text-rose-700 bg-rose-50">
                                Med Review: {String(row.medicationReview.priority).replace(/_/g, " ")}
                              </Badge>
                            )}
                            {row.polypharmacy && row.medicationReview?.follow_up_interval && (
                              <Badge variant="outline" className="text-[10px] border-rose-200 text-rose-700 bg-rose-50">
                                Follow-up: {row.medicationReview.follow_up_interval}
                              </Badge>
                            )}
                            {row.socialRiskConcern && (
                              <Badge variant="outline" className="text-[10px] border-orange-200 text-orange-700 bg-orange-50">Social Risk</Badge>
                            )}
                            {row.socialRiskConcern && row.sdohPlan?.priority && (
                              <Badge variant="outline" className="text-[10px] border-orange-200 text-orange-700 bg-orange-50">
                                SDOH: {String(row.sdohPlan.priority).replace(/_/g, " ")}
                              </Badge>
                            )}
                            {row.socialRiskConcern && row.sdohPlan?.follow_up_interval && (
                              <Badge variant="outline" className="text-[10px] border-orange-200 text-orange-700 bg-orange-50">
                                Follow-up: {row.sdohPlan.follow_up_interval}
                              </Badge>
                            )}
                            {row.cognitiveConcern && <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-700 bg-purple-50">Cognitive Follow-Up</Badge>}
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          {isUsDemoMode && row.demoVisitId ? (
                            <div className="flex flex-wrap gap-1">
                              <Button asChild variant="outline" size="sm">
                                <Link to={row.demoVisitHref || "/nueva-consulta?us_demo=1"}>Open Visit</Link>
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => openDemoPdf(row.demoVisitId, "clinical")}>Clinical</Button>
                              <Button variant="outline" size="sm" onClick={() => openDemoPdf(row.demoVisitId, "prevention")}>Prevention</Button>
                              <Button variant="outline" size="sm" onClick={() => openDemoPdf(row.demoVisitId, "caregiver")}>Caregiver</Button>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              <Button asChild variant="outline" size="sm">
                                <Link to={`/pacientes/${row.id}`}>Open</Link>
                              </Button>
                              {listStatus === "archived" ? (
                                <Button variant="outline" size="sm" onClick={() => handleRestorePatient(row.id)}>Restore</Button>
                              ) : (
                                <Button variant="outline" size="sm" onClick={() => handleArchivePatient(row.id)}>Archive</Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Pacientes</h1>
          <p className="text-slate-500 mt-1">
            {listStatus === "archived"
              ? `${safePatients.length} pacientes archivados`
              : `${safePatients.length} pacientes activos`}
          </p>
        </div>
        <div className="relative inline-block">
          <DemoTooltip message="Aquí están los pacientes del demo. Abre uno y explora su historial clínico, alertas y consultas anteriores." />
          <Button onClick={openNewPatientDialog} className="bg-slate-800 hover:bg-slate-700" data-testid="add-patient-btn" disabled={isDemoUser}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Paciente
          </Button>
        </div>
      </div>

      {!isPremium && safePatients.length >= 5 && (
        <UpgradePrompt message="Ha alcanzado el límite de 5 pacientes en el plan Lite." />
      )}

      {/* Search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={listStatus === "active" ? "default" : "outline"}
            size="sm"
            className={listStatus === "active" ? "bg-slate-800 hover:bg-slate-700" : ""}
            onClick={() => setListStatus("active")}
            data-testid="patients-filter-active"
          >
            Activos
          </Button>
          <Button
            variant={listStatus === "archived" ? "default" : "outline"}
            size="sm"
            className={listStatus === "archived" ? "bg-slate-800 hover:bg-slate-700" : ""}
            onClick={() => setListStatus("archived")}
            data-testid="patients-filter-archived"
          >
            Archivados
          </Button>
        </div>
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre o diagnóstico..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-patients-input"
          />
        </div>
      </div>
      {listStatus === "archived" && (
        <p className="text-xs text-slate-500 max-w-2xl">
          Los expedientes archivados conservan sus consultas y documentos. Debe restaurar al paciente para iniciar una nueva consulta.
        </p>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPatient ? "Editar Paciente" : "Registrar Nuevo Paciente"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombres">Nombres *</Label>
                <Input
                  id="nombres"
                  value={formData.nombres}
                  onChange={(e) => setFormData({ ...formData, nombres: e.target.value })}
                  required
                  data-testid="patient-nombres-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="apellidos">Apellidos *</Label>
                <Input
                  id="apellidos"
                  value={formData.apellidos}
                  onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                  required
                  data-testid="patient-apellidos-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fechaNac">Fecha de Nacimiento *</Label>
                <Input
                  id="fechaNac"
                  type="date"
                  value={formData.fecha_nacimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                  required
                  data-testid="patient-dob-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono *</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  required
                  data-testid="patient-phone-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  data-testid="patient-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cuidador">Cuidador principal</Label>
                <Input
                  id="cuidador"
                  value={formData.cuidador_principal}
                  onChange={(e) => setFormData({ ...formData, cuidador_principal: e.target.value })}
                  data-testid="patient-caregiver-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telCuidador">Teléfono del cuidador</Label>
                <Input
                  id="telCuidador"
                  value={formData.telefono_cuidador}
                  onChange={(e) => setFormData({ ...formData, telefono_cuidador: e.target.value })}
                  data-testid="patient-caregiver-phone-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="diagnostico">Diagnóstico principal *</Label>
                <Input
                  id="diagnostico"
                  value={formData.diagnostico_principal}
                  onChange={(e) => setFormData({ ...formData, diagnostico_principal: e.target.value })}
                  required
                  data-testid="patient-diagnosis-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="alergias">Alergias (separadas por coma)</Label>
              <Input
                id="alergias"
                value={formData.alergias}
                onChange={(e) => setFormData({ ...formData, alergias: e.target.value })}
                placeholder="Penicilina, Aspirina, etc."
                data-testid="patient-allergies-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-blue-500" />
                Condiciones Crónicas
              </Label>
              <MultiSelect
                options={CHRONIC_CONDITIONS_LIST}
                selected={formData.chronic_conditions}
                onChange={(values) => setFormData({ ...formData, chronic_conditions: values })}
                placeholder="Seleccionar condiciones crónicas..."
                searchPlaceholder="Buscar condición..."
                emptyText="No se encontró la condición"
                data-testid="patient-chronic-conditions-select"
              />
              <p className="text-xs text-muted-foreground">
                Seleccione las enfermedades crónicas del paciente para el sistema CDSS
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="medicamentos">Medicamentos actuales (separados por coma)</Label>
              <Textarea
                id="medicamentos"
                value={formData.medicamentos_actuales}
                onChange={(e) => setFormData({ ...formData, medicamentos_actuales: e.target.value })}
                placeholder="Metformina 850mg, Losartán 50mg, etc."
                data-testid="patient-medications-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-800 hover:bg-slate-700" disabled={saving} data-testid="save-patient-btn">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  editingPatient ? "Actualizar" : "Guardar"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Patients Grid or Empty */}
      {filteredPatients.length === 0 ? (
        <EmptyState
          icon={User}
          title={listStatus === "archived" ? "Sin pacientes archivados" : "Sin pacientes"}
          description={
            listStatus === "archived"
              ? "No hay expedientes archivados en esta cuenta."
              : "Registre su primer paciente para comenzar a gestionar consultas y evaluaciones geriátricas."
          }
          actionLabel={listStatus === "archived" ? undefined : "Nuevo Paciente"}
          onAction={listStatus === "archived" ? undefined : openNewPatientDialog}
        />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPatients.map((patient) => (
          <Card 
            key={patient.id} 
            className="border-slate-200 hover:shadow-lg transition-shadow group"
            data-testid={`patient-card-${patient.id}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                    <User className="h-6 w-6 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{patient.nombres} {patient.apellidos}</h3>
                    <p className="text-sm text-slate-500">{patient.edad} años</p>
                  </div>
                </div>
                <RiskBadge level={patient.nivel_riesgo || "bajo"} />
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="truncate">{patient.diagnostico_principal}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Phone className="h-4 w-4" />
                  <span>{patient.telefono}</span>
                </div>
                {patient.cuidador_principal && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <User className="h-4 w-4" />
                    <span className="truncate">{patient.cuidador_principal}</span>
                  </div>
                )}
                {patient.chronic_conditions?.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Activity className="h-4 w-4 text-blue-500" />
                    <span className="truncate">{patient.chronic_conditions.length} condición(es) crónica(s)</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditPatientDialog(patient)}
                    className="text-slate-600 hover:text-slate-800"
                    data-testid={`edit-patient-${patient.id}`}
                    disabled={isDemoUser}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {listStatus === "archived" ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-600 hover:text-slate-800"
                      onClick={() => handleRestorePatient(patient.id)}
                      data-testid={`restore-patient-${patient.id}`}
                      disabled={isDemoUser}
                      title="Restaurar paciente"
                    >
                      <ArchiveRestore className="h-4 w-4" />
                      <span className="sr-only">Restaurar paciente</span>
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-600 hover:text-slate-800"
                      onClick={() => handleArchivePatient(patient.id)}
                      data-testid={`archive-patient-${patient.id}`}
                      disabled={isDemoUser}
                      title="Archivar paciente"
                    >
                      <Archive className="h-4 w-4" />
                      <span className="sr-only">Archivar paciente</span>
                    </Button>
                  )}
                </div>
                <Link to={`/pacientes/${patient.id}`}>
                  <Button variant="ghost" size="sm" className="text-slate-600" data-testid={`view-patient-${patient.id}`}>
                    Ver perfil
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}
    </div>
  );
}
